/**
 * A benchmark's remediation read as steps (the compliance page's "How to fix it").
 *
 * CIS writes each remediation as one paragraph that runs the instruction, an
 * example and the commands together ("Edit /etc/default/grub and add audit=1 …
 * Example: GRUB_CMDLINE_LINUX="audit=1" Run the following command … : # update-grub").
 * People read that as a wall, and two checks that edit the same line look like they
 * disagree. This turns it into short numbered steps with the commands set apart, and
 * the dashboard keeps the benchmark's own words one click away.
 *
 * The audit checks get steps written for them, because their examples are partial
 * on purpose (each shows one option where the line needs both). Everything else is
 * split on the benchmark's own wording; nothing is invented, and a remediation that
 * will not split stays a paragraph.
 */
const AUDIT_BOOT = /^ensure (audit_backlog_limit is sufficient|auditing for processes that start prior to auditd is enabled)$/;
const AUDIT_COMMAND = /^ensure successful and unsuccessful attempts to use the (\S+) command are collected$/;
const normal = (title) => title.toLowerCase().replace(/\s+/g, ' ').trim().replace(/\.+$/, '').trim();
/** The examples run the command together with prose; tidy what is left of one. */
const clean = (text) => text.replace(/\s+/g, ' ').trim();
const AUDIT_BOOT_STEPS = [
    {
        text: 'Open the bootloader settings.',
        code: 'sudo nano /etc/default/grub',
    },
    {
        text: 'Find the line starting GRUB_CMDLINE_LINUX and add both options inside the quotes, after whatever is already there. This one change fixes both audit checks.',
        code: 'GRUB_CMDLINE_LINUX="quiet audit=1 audit_backlog_limit=8192"',
    },
    {
        text: 'Rebuild the boot menu, then restart when it suits you. The options only take effect after a restart.',
        code: 'sudo update-grub\nsudo reboot',
    },
];
function auditCommandSteps(name, remediation) {
    const path = /-F path=(\S+)/.exec(remediation)?.[1] ?? `/usr/bin/${name}`;
    const key = /-k (\S+)/.exec(remediation)?.[1] ?? 'perm_chng';
    return [
        {
            text: 'Make sure the audit service is installed (a restart is not needed for this).',
            code: 'sudo apt install auditd',
        },
        {
            text: `Add a rule that records who runs ${name}. Put this line in /etc/audit/rules.d/50-bartizo-baseline.rules (use 1000 if your users start at a different number, see UID_MIN in /etc/login.defs).`,
            code: `-a always,exit -F path=${path} -F perm=x -F auid>=1000 -F auid!=unset -k ${key}`,
        },
        {
            text: 'Load the rules.',
            code: 'sudo augenrules --load',
        },
        {
            text: 'Check they are active. If it prints enabled 2, the rules are locked and need a restart to change.',
            code: 'sudo auditctl -s | grep enabled',
        },
    ];
}
/** Where a sentence of benchmark prose begins a new instruction. */
const SPLIT = /\s(?=(?:Example:|Example script|Run the following|Run |Edit |Create |Load |Merge |Check |Verify |Set |Add |Remove |Install |Configure |Modify |Restart |Reload )\w?)/;
/**
 * Where an example's code stops and the benchmark's prose resumes ("… - IF - CVE-… has
 * not been reviewed …", "Note: First occurrence …"). Copied together, the prose would be
 * pasted into a configuration file, so the code ends at the first such marker and the
 * rest becomes a step of its own.
 */
const PROSE_AFTER_CODE = /\s+(?:-\s+)?(?:IF|If)\s|\s+Note:/;
function codeAndNote(text) {
    // The benchmark's text was a PDF: a break inside a word leaves "umac- 64".
    const joined = text.replace(/(?<=\w)-\s+(?=\w)/g, '-');
    const cut = PROSE_AFTER_CODE.exec(joined);
    if (cut === null)
        return { code: joined.trim() };
    const note = joined
        .slice(cut.index)
        .replace(/^\s*-\s+/, '')
        .trim();
    return { code: joined.slice(0, cut.index).replace(/\s+-$/, '').trim(), note };
}
export function genericSteps(remediation) {
    const steps = [];
    for (const part of remediation.trim().split(SPLIT)) {
        const piece = clean(part);
        if (piece === '')
            continue;
        // "Example: …" or "Example script to create a file: #!/usr/bin/env bash { … }".
        const example = /^(Example[^:]*:)\s*(.+)$/.exec(piece);
        if (example?.[1] !== undefined && example[2] !== undefined) {
            // A leading "# " is a shell prompt; "#!" is a script and stays.
            const { code, note } = codeAndNote(example[2].replace(/^#\s+/, ''));
            steps.push({ text: example[1] === 'Example:' ? 'For example:' : example[1], code });
            if (note !== undefined)
                steps.push({ text: note });
            continue;
        }
        // "…the following command: # update-grub." — the shell prompt starts the command.
        const command = /^(.*?):\s*#\s+(.+?)\.?$/.exec(piece);
        if (command?.[1] !== undefined && command[2] !== undefined) {
            const { code, note } = codeAndNote(command[2]);
            steps.push({ text: `${command[1]}:`, code });
            if (note !== undefined)
                steps.push({ text: note });
            continue;
        }
        steps.push({ text: piece });
    }
    return steps;
}
/**
 * Steps for a check, or none when the remediation is better shown as it was written
 * (a single sentence, say). `original` is always the benchmark's own text.
 */
export function plainFix(title, remediation) {
    const wanted = normal(title);
    if (AUDIT_BOOT.test(wanted))
        return [...AUDIT_BOOT_STEPS];
    const audit = AUDIT_COMMAND.exec(wanted);
    if (audit?.[1] !== undefined)
        return auditCommandSteps(audit[1], remediation);
    const steps = genericSteps(remediation);
    return steps.length >= 2 || steps.some((step) => step.code !== undefined) ? steps : [];
}
//# sourceMappingURL=plain-fix.js.map